import axios from 'axios';

// Email service configuration
const EMAIL_SERVICE_CONFIG = {
  // You can use services like EmailJS, SendGrid, or your own backend
  // For now, we'll create a service that can be easily integrated
  
  // Option 1: EmailJS (free tier available)
  emailjs: {
    serviceId: 'YOUR_EMAILJS_SERVICE_ID',
    templateId: 'YOUR_EMAILJS_TEMPLATE_ID',
    userId: 'YOUR_EMAILJS_USER_ID',
  },
  
  // Option 2: Your own backend endpoint
  backend: {
    url: process.env.REACT_APP_EMAIL_API_URL || 'https://your-backend.com/api/contact',
  },
  
  // Option 3: SendGrid (requires API key)
  sendgrid: {
    apiKey: process.env.REACT_APP_SENDGRID_API_KEY,
    url: 'https://api.sendgrid.com/v3/mail/send',
  },
};

/**
 * Send contact form email using EmailJS
 * @param {Object} formData - Contact form data
 * @returns {Promise}
 */
export const sendEmailViaEmailJS = async (formData) => {
  try {
    // This requires EmailJS library to be installed
    // npm install @emailjs/browser
    const emailjs = await import('@emailjs/browser');
    
    const templateParams = {
      to_email: 'charly@globaltrendpro.com',
      from_name: formData.name,
      from_email: formData.email,
      company: formData.company,
      phone: formData.phone,
      message: formData.message,
      subject: 'New Demo Request - HarvestAI',
    };

    const response = await emailjs.send(
      EMAIL_SERVICE_CONFIG.emailjs.serviceId,
      EMAIL_SERVICE_CONFIG.emailjs.templateId,
      templateParams,
      EMAIL_SERVICE_CONFIG.emailjs.userId
    );

    return { 
      success: true, 
      message: 'Thank you! We\'ll contact you soon to schedule your demo.',
      data: response 
    };
  } catch (error) {
    console.error('EmailJS error:', error);
    throw new Error('Failed to send email. Please try again later.');
  }
};

/**
 * Send contact form email via your backend
 * @param {Object} formData - Contact form data
 * @returns {Promise}
 */
export const sendEmailViaBackend = async (formData) => {
  try {
    const emailData = {
      to: 'charly@globaltrendpro.com',
      subject: 'New Demo Request - HarvestAI',
      html: `
        <h2>New Demo Request from HarvestAI Website</h2>
        <p><strong>Name:</strong> ${formData.name}</p>
        <p><strong>Email:</strong> ${formData.email}</p>
        <p><strong>Company:</strong> ${formData.company || 'Not provided'}</p>
        <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
        <p><strong>Message:</strong></p>
        <p>${formData.message}</p>
        <hr>
        <p><em>This email was sent from the HarvestAI contact form.</em></p>
      `,
      text: `
        New Demo Request from HarvestAI Website
        
        Name: ${formData.name}
        Email: ${formData.email}
        Company: ${formData.company || 'Not provided'}
        Phone: ${formData.phone || 'Not provided'}
        Message: ${formData.message}
        
        This email was sent from the HarvestAI contact form.
      `,
    };

    const response = await axios.post(EMAIL_SERVICE_CONFIG.backend.url, emailData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return { success: true, data: response.data };
  } catch (error) {
    console.error('Backend email error:', error);
    throw new Error('Failed to send email via backend');
  }
};

/**
 * Send contact form email via SendGrid
 * @param {Object} formData - Contact form data
 * @returns {Promise}
 */
export const sendEmailViaSendGrid = async (formData) => {
  try {
    const emailData = {
      personalizations: [
        {
          to: [{ email: 'charly@globaltrendpro.com', name: 'Charly' }],
          subject: 'New Demo Request - HarvestAI',
        },
      ],
      from: { email: 'noreply@harvestai.com', name: 'HarvestAI Contact Form' },
      content: [
        {
          type: 'text/html',
          value: `
            <h2>New Demo Request from HarvestAI Website</h2>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Email:</strong> ${formData.email}</p>
            <p><strong>Company:</strong> ${formData.company || 'Not provided'}</p>
            <p><strong>Phone:</strong> ${formData.phone || 'Not provided'}</p>
            <p><strong>Message:</strong></p>
            <p>${formData.message}</p>
            <hr>
            <p><em>This email was sent from the HarvestAI contact form.</em></p>
          `,
        },
      ],
    };

    const response = await axios.post(EMAIL_SERVICE_CONFIG.sendgrid.url, emailData, {
      headers: {
        'Authorization': `Bearer ${EMAIL_SERVICE_CONFIG.sendgrid.apiKey}`,
        'Content-Type': 'application/json',
      },
    });

    return { success: true, data: response.data };
  } catch (error) {
    console.error('SendGrid error:', error);
    throw new Error('Failed to send email via SendGrid');
  }
};

/**
 * Main email sending function - uses EmailJS by default
 * @param {Object} formData - Contact form data
 * @returns {Promise}
 */
export const sendContactEmail = async (formData) => {
  try {
    // Use EmailJS for sending emails
    return await sendEmailViaEmailJS(formData);
  } catch (error) {
    console.error('Email sending failed:', error);
    throw new Error('Failed to send email. Please try again later.');
  }
};

/**
 * Validate contact form data
 * @param {Object} formData - Contact form data
 * @returns {Object} - Validation result
 */
export const validateContactForm = (formData) => {
  const errors = {};
  
  if (!formData.name || formData.name.trim().length < 2) {
    errors.name = 'Name must be at least 2 characters long';
  }
  
  if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }
  
  if (!formData.message || formData.message.trim().length < 10) {
    errors.message = 'Message must be at least 10 characters long';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}; 